import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { getProductReviews } from "../../services/productsService";

import ReviewTile from "./ReviewTile.tsx";
import ReviewStars from "./ReviewStars.tsx";
import ReviewForm from "./ReviewForm.tsx";
import Button from "./core-components/Button.tsx";
import { translations } from "../../utils/translations.js";
import { useAuth } from "../../hooks/useAuth.jsx";
import { useNavigate } from "react-router-dom";
import { getUserIdFromToken, getUserRoleFromToken } from "../../utils/utils.ts";
import { toast, ToastContainer } from "react-toastify";
import { roles } from "../../utils/constants.ts";

const ReviewContainer = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ReviewHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
`;

const FormButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Header = styled.h4`
  color: var(--primary-brown);
  margin: 4px;
`;

const Title = styled.h2`
  color: var(--primary-brown);
  padding: 8px;
  border-bottom: 2px solid var(--background-green);
`;
interface Props {
  productId: string;
}

const Reviews: React.FC<Props> = ({ productId }) => {
  const languageCode = useSelector((state: any) => state.settings.languageCode);
  const [totalRating, setTotalRating] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();
  const userId = getUserIdFromToken(token);
  const userRole = getUserRoleFromToken(token);

  const { data: reviews } = useQuery({
    queryKey: ["productReviews", productId],
    queryFn: () => getProductReviews(productId),
  });

  useEffect(() => {
    let sum = 0;
    if (!reviews || reviews.length === 0) return;
    reviews.forEach((review) => {
      sum += review.starNumber;
    });

    setTotalRating(sum / reviews.length);
  }, [reviews]);

  const notify = () =>
    toast.success(translations[languageCode]["reviews-success"], {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "light",
    });

  const notifyError = () =>
    toast.error(translations[languageCode]["reviews-error"], {
      position: "top-center",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "light",
    });
  return (
    <ReviewContainer>
      <Title>{translations[languageCode]["reviews-title"]}</Title>

      <ReviewHeader>
        <ReviewStars
          rating={totalRating}
          reviewsNumber={reviews ? reviews.length : 0}
          isVertical
        />
        <FormButtonContainer>
          <Header>{translations[languageCode]["review-title-form"]}</Header>
          {isFormOpen ? (
            <Button
              id="close-review"
              onClick={() => setIsFormOpen(false)}
              text={translations[languageCode]["review-close-form"]}
              backgroundColor={"var(--primary-brown)"}
              color="white"
            />
          ) : (
            <Button
              id="save-review"
              onClick={() => {
                setIsFormOpen(true);
                if (!token || userRole !== roles.CLIENT) {
                  navigate(`/login`);
                } else {
                  setIsFormOpen(true);
                }
              }}
              text={translations[languageCode]["revie-open-form"]}
              backgroundColor={"var(--primary-brown)"}
              color="white"
            />
          )}
        </FormButtonContainer>
      </ReviewHeader>
      {isFormOpen && (
        <ReviewForm
          clientID={userId}
          productID={productId}
          onClosed={(type) => {
            if (type === "success") {
              notify();
            } else {
              notifyError();
            }
            setIsFormOpen(false);
          }}
        />
      )}
      {reviews &&
        reviews.map((review, index) => {
          return (
            <ReviewTile
              key={"review" + index}
              rating={review.starNumber}
              name={review.firstName + " " + review.lastName}
              review={review.message}
              date={review.date}
            />
          );
        })}
      <ToastContainer />
    </ReviewContainer>
  );
};

export default Reviews;
